import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Transfer {
  id: string;
  product: string;
  sku: string;
  quantity: number;
  fromLocation: string;
  toLocation: string;
  requestedBy: string;
  requestedAt: string;
  status: "pending" | "in_transit" | "completed" | "cancelled";
  expectedArrival: string;
}

const MOCK_TRANSFERS: Transfer[] = [
  { id: "TRF-001", product: "Premium Headphones", sku: "ELEC-001", quantity: 20, fromLocation: "Warehouse A", toLocation: "Store Front", requestedBy: "John Doe", requestedAt: "2024-01-15 08:30", status: "in_transit", expectedArrival: "2024-01-15 14:00" },
  { id: "TRF-002", product: "Wireless Mouse", sku: "ELEC-002", quantity: 50, fromLocation: "Warehouse B", toLocation: "Warehouse A", requestedBy: "Jane Smith", requestedAt: "2024-01-15 09:00", status: "pending", expectedArrival: "2024-01-16 10:00" },
  { id: "TRF-003", product: "Cotton T-Shirt", sku: "CLTH-001", quantity: 30, fromLocation: "Warehouse A", toLocation: "Store Front", requestedBy: "Mike Johnson", requestedAt: "2024-01-14 16:00", status: "completed", expectedArrival: "2024-01-15 09:00" },
  { id: "TRF-004", product: "Running Shoes", sku: "CLTH-003", quantity: 15, fromLocation: "Store Front", toLocation: "Warehouse B", requestedBy: "Sarah Wilson", requestedAt: "2024-01-14 14:30", status: "cancelled", expectedArrival: "-" },
  { id: "TRF-005", product: "USB-C Cable", sku: "ELEC-003", quantity: 100, fromLocation: "Warehouse B", toLocation: "Store Front", requestedBy: "John Doe", requestedAt: "2024-01-13 11:00", status: "completed", expectedArrival: "2024-01-14 12:00" },
];

export default function TransfersSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredTransfers = MOCK_TRANSFERS.filter((item) => {
    const matchesSearch =
      item.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || item.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const pendingTransfers = MOCK_TRANSFERS.filter((t) => t.status === "pending").length;
  const inTransitTransfers = MOCK_TRANSFERS.filter((t) => t.status === "in_transit").length;
  const completedToday = MOCK_TRANSFERS.filter((t) => t.status === "completed" && t.requestedAt.includes("2024-01-15")).length;
  const totalItemsInTransit = MOCK_TRANSFERS.filter((t) => t.status === "in_transit").reduce((acc, t) => acc + t.quantity, 0);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return { color: "text-emerald-500 bg-emerald-500/20", label: t("posInventory.completed"), icon: "ri-check-line" };
      case "pending":
        return { color: "text-amber-500 bg-amber-500/20", label: t("posInventory.pending"), icon: "ri-time-line" };
      case "in_transit":
        return { color: "text-blue-500 bg-blue-500/20", label: t("posInventory.inTransit"), icon: "ri-truck-line" };
      case "cancelled":
        return { color: "text-red-500 bg-red-500/20", label: t("posInventory.cancelled"), icon: "ri-close-line" };
      default:
        return { color: "text-gray-500 bg-gray-500/20", label: status, icon: "ri-question-line" };
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posInventory.transfersTitle")}</h1>
          <p className={themeClasses.textSecondary}>{t("posInventory.transfersSubtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-download-2-line"></i>
            {t("posInventory.export")}
          </button>
          <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
            <i className="ri-add-line"></i>
            {t("posInventory.newTransfer")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.pendingTransfers")}</span>
            <i className="ri-time-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>{pendingTransfers}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.inTransit")}</span>
            <i className="ri-truck-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-blue-500`}>{inTransitTransfers}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.itemsInTransit")}</span>
            <i className="ri-box-3-line text-violet-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalItemsInTransit}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.completedToday")}</span>
            <i className="ri-check-double-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>{completedToday}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posInventory.searchTransfers")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              <option value="all">{t("posInventory.allStatuses")}</option>
              <option value="pending">{t("posInventory.pending")}</option>
              <option value="in_transit">{t("posInventory.inTransit")}</option>
              <option value="completed">{t("posInventory.completed")}</option>
              <option value="cancelled">{t("posInventory.cancelled")}</option>
            </select>
            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-filter-line"></i>
              {t("posInventory.filters")}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.transferId")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.product")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.quantity")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.from")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.to")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.status")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.expectedArrival")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransfers.map((item) => {
                const statusBadge = getStatusBadge(item.status);
                return (
                  <tr key={item.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                    <td className={`px-4 py-3 font-mono text-sm ${themeClasses.text}`}>{item.id}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className={`font-medium ${themeClasses.text}`}>{item.product}</p>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>{item.sku}</p>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${themeClasses.text}`}>{item.quantity}</td>
                    <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>
                      <div className="flex items-center gap-1">
                        <i className="ri-map-pin-line text-red-400"></i>
                        {item.fromLocation}
                      </div>
                    </td>
                    <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>
                      <div className="flex items-center gap-1">
                        <i className="ri-map-pin-line text-emerald-400"></i>
                        {item.toLocation}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${statusBadge.color}`}>
                        <i className={statusBadge.icon}></i>
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>{item.expectedArrival}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button aria-label={t("general.viewDetails", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                          <i className="ri-eye-line"></i>
                        </button>
                        {item.status === "pending" && (
                          <button aria-label={t("general.close", { ns: "common" })} className="p-2 rounded-lg hover:bg-red-500/20 text-red-500">
                            <i className="ri-close-line"></i>
                          </button>
                        )}
                        {item.status === "in_transit" && (
                          <button className="p-2 rounded-lg hover:bg-emerald-500/20 text-emerald-500">
                            <i className="ri-check-line"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
